import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styles from './ProfilePage.module.css';
import BrandMetadataModal from '../components/BrandMetadataModal';
import DataFallback from '../components/feedback/DataFallback';
import { classifyError } from '../utils/errorTaxonomy';
import { resilientFetch, TIMEOUT_NORMAL } from '../utils/resilientFetch';

function ProfilePage() {
  const dispatch = useDispatch();
  const status = useSelector((state) => state.user.status);
  const [profile, setProfile] = useState(null);
  const [showBrandMetadataModal, setShowBrandMetadataModal] = useState(false);
  const [brandId, setBrandId] = useState(null);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      setError(null);
      resilientFetch(
        process.env.REACT_APP_API_BASE_URL + '/api/auth/users/',
        { headers: { Authorization: `Bearer ${token}` } },
        { timeoutMs: TIMEOUT_NORMAL, fetchImpl: (u, o) => fetch(u, o) }
      )
        // `res.ok ? res.json() : null` swallowed every failure, so `profile`
        // stayed null and the page said "Loading..." forever — the infinite
        // spinner in its purest form.
        .then(res => {
          if (!res.ok) {
            const err = new Error(`HTTP ${res.status}`);
            err.status = res.status;
            throw err;
          }
          return res.json();
        })
        .then(data => {
          if (data && data.user) {
            setProfile(data);
            console.log('Profile data:', data); // Debug log
            // NEW API FORMAT: data.user.brand.id
            // For users, get the brand_id directly from the user data
            if (data.user.brand && data.user.brand.id) {
              console.log('Setting brandId:', data.user.brand.id); // Debug log
              setBrandId(data.user.brand.id);
            }
          }
        })
        .catch(err => {
          setError(err && err.classified ? err.classified : classifyError(err));
        });
    }
  }, [dispatch, attempt]);

  if (error && !profile) {
    return (
      <div style={{ padding: '2rem' }}>
        <DataFallback
          variant="pane"
          error={error}
          onRetry={() => setAttempt((n) => n + 1)}
        />
      </div>
    );
  }

  if (status === 'loading' || !profile) {
    return (
      <div style={{ padding: '2rem', maxWidth: 640 }}>
        Loading...
      </div>
    );
  }

  const user = profile.user;

  // Demo activity and action items (replace with real data as needed)
  const activityLog = [
    { date: '2025-06-15', action: 'Logged in' },
    { date: '2025-06-15', action: 'Viewed Brand CRM Dashboard' },
    { date: '2025-06-15', action: 'Updated product listing' },
    { date: '2025-06-14', action: 'Joined Community Forum' },
    { date: '2025-06-13', action: 'Completed Sustainability Questionnaire' },
  ];
  const actionItems = [
    { id: 1, title: 'Complete onboarding tasks', due: '2025-06-20' },
    { id: 2, title: 'Update brand profile', due: '2025-06-22' },
    { id: 3, title: 'Respond to support ticket', due: '2025-06-18' },
  ];

  return (
    <div className={styles.profilePage}>
      <div className={styles.profileCard}>
        <img src={`https://ui-avatars.com/api/?name=${user.first_name}+${user.last_name}`} alt="Profile" className={styles.avatar} />
        <div className={styles.infoSection}>
          <h2 className={styles.name}>{user.first_name} {user.last_name}</h2>
          <div className={styles.role}>{profile.user_type} @ <span className={styles.brand}>{user.brand?.name || 'No Brand'}</span></div>
          <div className={styles.email}>{user.email}</div>
          <div className={styles.phone}>{user.phone_number}</div>
        </div>
      </div>
      <div className={styles.bioSection}>
        <h3>About</h3>
        <p>{user.bio || 'No bio available.'}</p>
      </div>
      
      {/* Brand Metadata Section for appusers */}
      {(() => {
        const shouldShow = profile.user_type === 'appuser' && brandId;
        console.log('Brand metadata section - user_type:', profile.user_type, 'brandId:', brandId, 'shouldShow:', shouldShow);
        return shouldShow;
      })() && (
        <div className={styles.brandMetadataSection}>
          <h3>Brand Management</h3>
          <p>Manage your brand's information and settings.</p>
          <button 
            className={styles.actionBtn}
            onClick={() => setShowBrandMetadataModal(true)}
          >
            Manage Brand Metadata
          </button>
        </div>
      )}
      {/* Action Items Widget */}
      {/* <div className={styles.actionItemsWidget}>
        <h3>Action Items</h3>
        <ul>
          {actionItems.map(item => (
            <li key={item.id} className={styles.actionItem}>
              <span>{item.title}</span>
              <span className={styles.actionDue}>Due: {item.due}</span>
            </li>
          ))}
        </ul>
      </div> */}
      {/* Activity Log View */}
      {/* <div className={styles.activityLogWidget}>
        <h3>Recent Activity</h3>
        <ul>
          {activityLog.map((log, idx) => (
            <li key={idx} className={styles.activityLogItem}>
              <span className={styles.activityDate}>{log.date}</span>
              <span className={styles.activityAction}>{log.action}</span>
            </li>
          ))}
        </ul>
      </div> */}
      {/* <div className={styles.actionsSection}>
        <button className={styles.actionBtn}>Edit Profile</button>
        <button className={styles.actionBtn}>Change Password</button>
        <button className={styles.actionBtn}>Logout</button>
      </div> */}
      
      {/* Brand Metadata Modal */}
      <BrandMetadataModal
        show={showBrandMetadataModal}
        onHide={() => setShowBrandMetadataModal(false)}
        brandId={brandId}
        onUpdateSuccess={(updatedBrand) => {
          console.log('Brand metadata updated:', updatedBrand);
          // Optionally refresh profile data here
        }}
      />
    </div>
  );
}

export default ProfilePage;